import { z } from 'zod';
// Request schemas for /api/auth/* routes.
//
// NOTE: the phone-bearing PATCH /me/profile schema lives in `profile-requests.ts`
// (not here) so the login / MFA / change-password lazy chunk never imports
// libphonenumber-js. See CONVENTIONS §Schemas (import-isolation split).
// Bodies are application/json only; CSRF defense via SameSite=Lax + the 415
// Content-Type check enforced in core/errors hook (ADR-003 §CSRF posture).
//
// String fields use .trim().min(1).max(N) where N matches the DB column
// length exactly (CONVENTIONS §Schemas — string fields rule). Password fields
// carry a generous .max(256) cap — not a DB-column bound (passwords are hashed,
// never stored raw) but a guard against argon2 CPU/memory amplification from an
// oversized input (the 1 MB body limit would otherwise be the only ceiling).
// ── login ──────────────────────────────────────────────────────────────────
// `identifier` = email OR username (identifier-first login, ADR-054 §F). The server
// resolves it the same way discovery does: a value containing '@' is matched
// against `users.email`, otherwise against `users.username`. Max 255 spans both
// (username ≤100, email ≤128). The login screen only reaches this step for
// non-SSO identifiers (an SSO email is routed to its IdP before any password).
export const LoginRequestSchema = z
    .object({
    identifier: z.string().trim().min(1).max(255),
    password: z.string().min(1).max(256),
})
    .strict();
// ── MFA verify (login-time, post password + pre session) ──────────────────
export const MfaVerifyRequestSchema = z
    .object({
    mfa_token: z.string().min(1),
    code: z
        .string()
        .regex(/^\d{6}$/, 'CODE_SIX_DIGITS'),
})
    .strict();
// ── MFA enroll (session-required; server returns secret + QR) ─────────────
// No body — the server generates and returns the secret.
// ── MFA enroll/verify (confirms the TOTP code, persists encrypted secret) ─
export const MfaEnrollVerifyRequestSchema = z
    .object({
    code: z.string().regex(/^\d{6}$/, 'CODE_SIX_DIGITS'),
})
    .strict();
// ── MFA disable (session-required; password re-confirmation) ──────────────
// POST not DELETE — DELETE-with-body has uneven client support; trading REST
// purity for compat.
export const MfaDisableRequestSchema = z
    .object({
    password: z.string().min(1).max(256),
    // A fresh TOTP is required in addition to the password (APP-244): disabling
    // the second factor is exactly the action a password-compromised session must
    // not perform alone. Same 6-digit shape as MfaVerify.
    code: z.string().regex(/^\d{6}$/, 'CODE_SIX_DIGITS'),
})
    .strict();
// ── change-password ───────────────────────────────────────────────────────
export const ChangePasswordRequestSchema = z
    .object({
    current_password: z.string().min(1).max(256),
    new_password: z.string().min(8).max(256),
})
    .strict();
// ── password reset (self-service "forgot password") ───────────────────────
// `identifier` = email OR username, resolved exactly like login (branch-on-'@').
// The request is STATELESS w.r.t. account existence — the response is uniform
// whether or not an account exists (SECURITY §Resource-enumeration), so this
// schema validates shape only. Max 255 spans username (≤100) + email (≤128).
export const ForgotPasswordRequestSchema = z
    .object({
    identifier: z.string().trim().min(1).max(255),
})
    .strict();
// ── password reset (completion) ───────────────────────────────────────────
// `token` is the opaque value from the email link (validated server-side against
// the single-use hash). `new_password` mirrors change-password's floor (min 8 —
// the backend hash accepts any input; the floor is the only policy at foundation).
export const ResetPasswordRequestSchema = z
    .object({
    token: z.string().min(1),
    new_password: z.string().min(8).max(256),
})
    .strict();
// ── logout ────────────────────────────────────────────────────────────────
// No body — the session cookie identifies what to revoke.
